import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseAuth, getFirestoreDb } from './config';
import type { User } from '../../types';
import type { RegisterFormData, LoginFormData, UserProfile } from '../../types/user';

/**
 * Registers a new user with email and password.
 * Creates user profile in Firestore.
 */
export async function registerUser(data: RegisterFormData): Promise<User> {
  const auth = getFirebaseAuth();
  const db = getFirestoreDb();

  const credential = await createUserWithEmailAndPassword(
    auth,
    data.email,
    data.password
  );

  await updateProfile(credential.user, {
    displayName: data.displayName,
  });

  const userProfile: UserProfile = {
    uid: credential.user.uid,
    email: data.email,
    displayName: data.displayName,
    avatarId: '',
    avatarUrl: credential.user.photoURL || '',
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  await setDoc(doc(db, 'users', credential.user.uid), {
    ...userProfile,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return buildUserFromFirebase(credential.user);
}

/**
 * Signs in a user with Google OAuth.
 */
export async function loginWithGoogle(): Promise<User> {
  const auth = getFirebaseAuth();
  const db = getFirestoreDb();
  const provider = new GoogleAuthProvider();

  const credential = await signInWithPopup(auth, provider);
  await ensureUserProfile(db, credential.user);

  return buildUserFromFirebase(credential.user);
}

/**
 * Signs in a user with email and password.
 */
export async function loginUser(data: LoginFormData): Promise<User> {
  const auth = getFirebaseAuth();

  const credential = await signInWithEmailAndPassword(
    auth,
    data.email,
    data.password
  );

  return buildUserFromFirebase(credential.user);
}

/**
 * Signs out the current user.
 */
export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  await firebaseSignOut(auth);
}

/**
 * Gets the user profile from Firestore.
 */
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const db = getFirestoreDb();
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as UserProfile;
  }

  return null;
}

/**
 * Gets the user profile from Firestore by email.
 */
export async function getUserProfileByEmail(email: string): Promise<UserProfile | null> {
  const trimmed = email.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.toLowerCase();
  const normalizedProfile = await fetchUserProfileByEmail(normalized);
  if (normalizedProfile || normalized === trimmed) {
    return normalizedProfile;
  }

  return await fetchUserProfileByEmail(trimmed);
}

/**
 * Subscribes to auth state changes.
 */
export function subscribeToAuthState(
  callback: (user: User | null) => void
): () => void {
  const auth = getFirebaseAuth();

  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      callback(null);
      return;
    }

    const user = await buildUserFromFirebase(firebaseUser);
    callback(user);
  });
}

/**
 * Converts Firebase User to our User type.
 */
function firebaseUserToUser(firebaseUser: FirebaseUser, avatarUrl?: string): User {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName || 'Anonymous',
    avatarUrl: avatarUrl || firebaseUser.photoURL || undefined,
    createdAt: new Date(firebaseUser.metadata.creationTime || Date.now()),
  };
}

/**
 * Resolves the avatar URL for a user.
 */
async function resolveUserAvatar(firebaseUser: FirebaseUser): Promise<string> {
  if (firebaseUser.photoURL) {
    return firebaseUser.photoURL;
  }

  const profile = await getUserProfile(firebaseUser.uid);
  return profile?.avatarUrl || '';
}

/**
 * Builds a User payload from Firebase data.
 */
async function buildUserFromFirebase(firebaseUser: FirebaseUser): Promise<User> {
  try {
    const avatarUrl = await resolveUserAvatar(firebaseUser);
    return firebaseUserToUser(firebaseUser, avatarUrl);
  } catch {
    return firebaseUserToUser(firebaseUser);
  }
}

/**
 * Ensures a user profile exists for the authenticated user.
 */
async function ensureUserProfile(
  db: ReturnType<typeof getFirestoreDb>,
  firebaseUser: FirebaseUser
): Promise<void> {
  const profileRef = doc(db, 'users', firebaseUser.uid);
  const existing = await getDoc(profileRef);

  if (existing.exists()) {
    const data = existing.data() as UserProfile;
    const updates: Partial<UserProfile> = {};

    if (firebaseUser.photoURL && !data.avatarUrl) {
      updates.avatarUrl = firebaseUser.photoURL;
    }

    if (firebaseUser.displayName && !data.displayName) {
      updates.displayName = firebaseUser.displayName;
    }

    if (Object.keys(updates).length > 0) {
      await updateDoc(profileRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    }

    return;
  }

  const displayName =
    firebaseUser.displayName ||
    firebaseUser.email?.split('@')[0] ||
    'User';

  const profile: UserProfile = {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName,
    avatarId: '',
    avatarUrl: firebaseUser.photoURL || '',
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  await setDoc(profileRef, {
    ...profile,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Updates the user's avatar URL in Firebase Auth and Firestore.
 */
export async function updateUserAvatar(avatarUrl: string): Promise<User> {
  const auth = getFirebaseAuth();
  const db = getFirestoreDb();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Not authenticated');
  }

  await updateProfile(user, { photoURL: avatarUrl });
  await updateDoc(doc(db, 'users', user.uid), {
    avatarUrl,
    updatedAt: serverTimestamp(),
  });

  return buildUserFromFirebase(user);
}

async function fetchUserProfileByEmail(email: string): Promise<UserProfile | null> {
  const db = getFirestoreDb();
  const usersRef = collection(db, 'users');
  const lookup = query(usersRef, where('email', '==', email), limit(1));
  const snapshot = await getDocs(lookup);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  const profile = docSnap.data() as UserProfile;
  if (!profile.uid) {
    profile.uid = docSnap.id;
  }
  return profile;
}

/**
 * Updates the user's display name in Firebase Auth and Firestore.
 */
export async function updateUserDisplayName(displayName: string): Promise<User> {
  const auth = getFirebaseAuth();
  const db = getFirestoreDb();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Not authenticated');
  }

  const trimmed = displayName.trim();
  if (!trimmed) {
    throw new Error('Display name cannot be empty');
  }

  await updateProfile(user, { displayName: trimmed });
  await updateDoc(doc(db, 'users', user.uid), {
    displayName: trimmed,
    updatedAt: serverTimestamp(),
  });

  return buildUserFromFirebase(user);
}
